/**
 * Lottie Registry
 * 
 * Centralized registry of all Lottie animations in the app.
 * 
 * HOW TO ADD NEW LOTTIES:
 * 1. Add your .json file to /assets/lottie/
 * 2. Add an entry to LOTTIE_REGISTRY below with:
 *    - key: filename without .json extension
 *    - name: Display name (Dutch)
 *    - category: One of the categories below
 *    - source: require() statement pointing to the file
 * 
 * That's it! The new Lottie will be available everywhere in the app.
 * 
 * NOTE: Explainer animations (for onboarding) are kept separate and not
 * included in this registry. They are managed in the onboarding module.
 */

/**
 * Lottie categories for filtering
 */
export type LottieCategory = 
  | "emoji" 
  | "animal" 
  | "celebration" 
  | "action" 
  | "object"
  | "people"
  | "fruit"
  | "monster";

/**
 * Lottie item interface
 */
export interface LottieItem {
  /** Unique key (filename without extension) */
  key: string;
  /** Display name (Dutch) */
  name: string;
  /** Category for filtering */
  category: LottieCategory;
  /** The require() source for LottieView */
  source: any;
}

/**
 * Category display names (Dutch)
 */
export const LOTTIE_CATEGORY_LABELS: Record<LottieCategory, string> = {
  emoji: "Emoji's",
  animal: "Dieren",
  celebration: "Feest",
  action: "Acties",
  object: "Objecten",
  people: "Mensen",
  fruit: "Fruit",
  monster: "Monsters",
};

/**
 * Category colors for UI
 */
export const LOTTIE_CATEGORY_COLORS: Record<LottieCategory, string> = {
  emoji: "#F59E0B",
  animal: "#10B981",
  celebration: "#EC4899",
  action: "#3B82F6",
  object: "#8B5CF6",
  people: "#6366F1",
  fruit: "#EF4444",
  monster: "#14B8A6",
};

/**
 * Main Lottie Registry
 * Add new Lotties here when you add them to /assets/lottie/
 */
export const LOTTIE_REGISTRY: LottieItem[] = [
  // === EMOJI's ===
  { key: "Astonished", name: "Verbaasd", category: "emoji", source: require("@/assets/lottie/Astonished.json") },
  { key: "Blowing_Kiss_Emoji", name: "Kusje", category: "emoji", source: require("@/assets/lottie/Blowing_Kiss_Emoji.json") },
  { key: "Clapping_emoji", name: "Applaus", category: "emoji", source: require("@/assets/lottie/Clapping_emoji.json") },
  { key: "Clown_Emoji", name: "Clown", category: "emoji", source: require("@/assets/lottie/Clown_Emoji.json") },
  { key: "Cool_Emoji", name: "Cool Emoji", category: "emoji", source: require("@/assets/lottie/Cool_Emoji.json") },
  { key: "Crazy_Face", name: "Gek Gezicht", category: "emoji", source: require("@/assets/lottie/Crazy_Face.json") },
  { key: "Disguised_Emoji", name: "Vermomd", category: "emoji", source: require("@/assets/lottie/Disguised_Emoji.json") },
  { key: "Grinning_Cat_Emoji", name: "Grijnzende Kat", category: "emoji", source: require("@/assets/lottie/Grinning_Cat_Emoji.json") },
  { key: "Heart_Hands", name: "Hartjes Handen", category: "emoji", source: require("@/assets/lottie/Heart_Hands.json") },
  { key: "Holding_Back_Tears", name: "Tranen Inhouden", category: "emoji", source: require("@/assets/lottie/Holding_Back_Tears.json") },
  { key: "In_Clouds_Emoji", name: "In de Wolken", category: "emoji", source: require("@/assets/lottie/In_Clouds_Emoji.json") },
  { key: "Laughing", name: "Lachen", category: "emoji", source: require("@/assets/lottie/Laughing.json") },
  { key: "Loudly_Crying_Emoji", name: "Hard Huilen", category: "emoji", source: require("@/assets/lottie/Loudly_Crying_Emoji.json") },
  { key: "Lying_Emoji", name: "Liegen", category: "emoji", source: require("@/assets/lottie/Lying_Emoji.json") },
  { key: "Nerd_Emoji", name: "Nerd", category: "emoji", source: require("@/assets/lottie/Nerd_Emoji.json") },
  { key: "Partying_Emoji", name: "Feest Emoji", category: "emoji", source: require("@/assets/lottie/Partying_Emoji.json") },
  { key: "bunny_emoji", name: "Konijn Emoji", category: "emoji", source: require("@/assets/lottie/bunny_emoji.json") },
  { key: "cool", name: "Cool", category: "emoji", source: require("@/assets/lottie/cool.json") },
  { key: "finger_crossed", name: "Vingers Gekruist", category: "emoji", source: require("@/assets/lottie/finger_crossed.json") },
  { key: "head_with_crown_emoji", name: "Kroon", category: "emoji", source: require("@/assets/lottie/head_with_crown_emoji.json") },
  { key: "love_gesture", name: "Liefde Gebaar", category: "emoji", source: require("@/assets/lottie/love_gesture.json") },
  { key: "moon_emoji", name: "Maan", category: "emoji", source: require("@/assets/lottie/moon_emoji.json") },
  { key: "muscle_emoji", name: "Spieren", category: "emoji", source: require("@/assets/lottie/muscle_emoji.json") },
  { key: "notes_emoji", name: "Muziek Noten", category: "emoji", source: require("@/assets/lottie/notes_emoji.json") },

  // === ANIMALS ===
  { key: "artic-fox", name: "Poolsvos", category: "animal", source: require("@/assets/lottie/artic-fox.json") },
  { key: "bear", name: "Beer", category: "animal", source: require("@/assets/lottie/bear.json") },
  { key: "butterfly", name: "Vlinder", category: "animal", source: require("@/assets/lottie/butterfly.json") },
  { key: "cheetah", name: "Cheetah", category: "animal", source: require("@/assets/lottie/cheetah.json") },
  { key: "chicken", name: "Kip", category: "animal", source: require("@/assets/lottie/chicken.json") },
  { key: "cow", name: "Koe", category: "animal", source: require("@/assets/lottie/cow.json") },
  { key: "crab", name: "Krab", category: "animal", source: require("@/assets/lottie/crab.json") },
  { key: "dog", name: "Hond", category: "animal", source: require("@/assets/lottie/dog.json") },
  { key: "dolphin", name: "Dolfijn", category: "animal", source: require("@/assets/lottie/dolphin.json") },
  { key: "elephant", name: "Olifant", category: "animal", source: require("@/assets/lottie/elephant.json") },
  { key: "emu", name: "Emoe", category: "animal", source: require("@/assets/lottie/emu.json") },
  { key: "fox", name: "Vos", category: "animal", source: require("@/assets/lottie/fox.json") },
  { key: "llama", name: "Lama", category: "animal", source: require("@/assets/lottie/llama.json") },

  // === FRUIT ===
  { key: "Banana", name: "Banaan", category: "fruit", source: require("@/assets/lottie/Banana.json") },
  { key: "Blueberry", name: "Blauwe Bes", category: "fruit", source: require("@/assets/lottie/Blueberry.json") },
  { key: "Cherry", name: "Kers", category: "fruit", source: require("@/assets/lottie/Cherry.json") },
  { key: "Clementine", name: "Clementine", category: "fruit", source: require("@/assets/lottie/Clementine.json") },
  { key: "Dragon fruit", name: "Drakenfruit", category: "fruit", source: require("@/assets/lottie/Dragon fruit.json") },
  { key: "Green_Apple", name: "Groene Appel", category: "fruit", source: require("@/assets/lottie/Green_Apple.json") },
  { key: "Mango", name: "Mango", category: "fruit", source: require("@/assets/lottie/Mango.json") },
  { key: "Orange", name: "Sinaasappel", category: "fruit", source: require("@/assets/lottie/Orange.json") },
  { key: "Strawberry", name: "Aardbei", category: "fruit", source: require("@/assets/lottie/Strawberry.json") },

  // === MONSTERS ===
  { key: "Black_monster", name: "Zwart Monster", category: "monster", source: require("@/assets/lottie/Black_monster.json") },
  { key: "Blue_monster", name: "Blauw Monster", category: "monster", source: require("@/assets/lottie/Blue_monster.json") },
  { key: "Gray_monster", name: "Grijs Monster", category: "monster", source: require("@/assets/lottie/Gray_monster.json") },
  { key: "Green_monster", name: "Groen Monster", category: "monster", source: require("@/assets/lottie/Green_monster.json") },
  { key: "Lime_monster", name: "Limoen Monster", category: "monster", source: require("@/assets/lottie/Lime_monster.json") },
  { key: "Pink_monster", name: "Roze Monster", category: "monster", source: require("@/assets/lottie/Pink_monster.json") },
  { key: "Purple_monster", name: "Paars Monster", category: "monster", source: require("@/assets/lottie/Purple_monster.json") },
  { key: "Red_monster", name: "Rood Monster", category: "monster", source: require("@/assets/lottie/Red_monster.json") },
  { key: "Yellow_monster", name: "Geel Monster", category: "monster", source: require("@/assets/lottie/Yellow_monster.json") },

  // === CELEBRATION ===
  { key: "balloons", name: "Ballonnen", category: "celebration", source: require("@/assets/lottie/balloons.json") },
  { key: "cake", name: "Taart", category: "celebration", source: require("@/assets/lottie/cake.json") },
  { key: "cheers", name: "Proost", category: "celebration", source: require("@/assets/lottie/cheers.json") },
  { key: "confetti", name: "Confetti", category: "celebration", source: require("@/assets/lottie/confetti.json") },
  { key: "party", name: "Feest", category: "celebration", source: require("@/assets/lottie/party.json") },
  { key: "party-blower", name: "Feest Toetertje", category: "celebration", source: require("@/assets/lottie/party-blower.json") },
  { key: "party-invitation", name: "Uitnodiging", category: "celebration", source: require("@/assets/lottie/party-invitation.json") },
  { key: "pinata", name: "Piñata", category: "celebration", source: require("@/assets/lottie/pinata.json") },
  { key: "Success", name: "Succes", category: "celebration", source: require("@/assets/lottie/Success.json") },
  { key: "completed", name: "Voltooid", category: "celebration", source: require("@/assets/lottie/completed.json") },
  { key: "Event_success", name: "Event Succes", category: "celebration", source: require("@/assets/lottie/Event_success.json") },

  // === ACTIONS ===
  { key: "1_Plus", name: "Plus 1", category: "action", source: require("@/assets/lottie/1_Plus.json") },
  { key: "camera", name: "Camera", category: "action", source: require("@/assets/lottie/camera.json") },
  { key: "download", name: "Download", category: "action", source: require("@/assets/lottie/download.json") },
  { key: "Chat", name: "Chat", category: "action", source: require("@/assets/lottie/Chat.json") },
  { key: "Interaction", name: "Interactie", category: "action", source: require("@/assets/lottie/Interaction.json") },
  { key: "Personal_input", name: "Persoonlijke Input", category: "action", source: require("@/assets/lottie/Personal_input.json") },

  // === OBJECTS ===
  { key: "alarm_clock", name: "Wekker", category: "object", source: require("@/assets/lottie/alarm_clock.json") },
  { key: "calendar", name: "Kalender", category: "object", source: require("@/assets/lottie/calendar.json") },
  { key: "grill", name: "Barbecue", category: "object", source: require("@/assets/lottie/grill.json") },
  { key: "stage", name: "Podium", category: "object", source: require("@/assets/lottie/stage.json") },
  { key: "ai-photo-editor", name: "Foto Editor", category: "object", source: require("@/assets/lottie/ai-photo-editor.json") },
  { key: "Photo", name: "Foto", category: "object", source: require("@/assets/lottie/Photo.json") },

  // === PEOPLE ===
  { key: "Colleagues", name: "Collega's", category: "people", source: require("@/assets/lottie/Colleagues.json") },
  { key: "Group", name: "Groep", category: "people", source: require("@/assets/lottie/Group.json") },
  { key: "Team_meeting", name: "Team Meeting", category: "people", source: require("@/assets/lottie/Team_meeting.json") },
  { key: "dj", name: "DJ", category: "people", source: require("@/assets/lottie/dj.json") },
  { key: "man", name: "Man", category: "people", source: require("@/assets/lottie/man.json") },
  { key: "women", name: "Vrouw", category: "people", source: require("@/assets/lottie/women.json") },
];

/**
 * Get all Lottie items
 */
export const getAllLotties = (): LottieItem[] => LOTTIE_REGISTRY;

/**
 * Get Lottie items by category
 */
export const getLottiesByCategory = (category: LottieCategory): LottieItem[] => 
  LOTTIE_REGISTRY.filter(item => item.category === category);

/**
 * Get a single Lottie by key
 */
export const getLottieByKey = (key: string): LottieItem | undefined => 
  LOTTIE_REGISTRY.find(item => item.key === key);

/**
 * Get Lottie source by key (for LottieView)
 * Returns undefined if not found
 */
export const getLottieSource = (key: string): any | undefined => 
  getLottieByKey(key)?.source;

/**
 * Get all unique categories
 */
export const getAllCategories = (): LottieCategory[] => 
  [...new Set(LOTTIE_REGISTRY.map(item => item.category))];

/**
 * Search Lotties by name
 */
export const searchLotties = (query: string): LottieItem[] => {
  const lowerQuery = query.toLowerCase();
  return LOTTIE_REGISTRY.filter(item => 
    item.name.toLowerCase().includes(lowerQuery) ||
    item.key.toLowerCase().includes(lowerQuery)
  );
};
